import { forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import * as React from "react";
import TransactionAuditTrailTimeline from "./transaction.audittrail.timeline";
import { Close, Info } from "@mui/icons-material";
import {
  GetDealerPlatformFeeOption,
  GetDealerHostUrl,
  TargetPayApi
} from "../../utils/UtilTarget";
import "./transaction.style.css";
import AuditTrailTimelineHelp from "./transaction.audittrail.help";
import { convertToUSD, isEmpty, Callback } from "../../utils/Util";
import "../../utils/string.extension";
import ProgressLoader from "../../common/components/progress.loader";
import PaymentStatusColumn from "../../common/components/paymentstatus.column";
import { PaymentStatus } from "../../types/enums";
import { useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { ProcessingMethodLabels } from "../../mappings/processing-method.mapping";

const wrapTextStyle: React.CSSProperties = {
  maxWidth: "100%",
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word"
};

const TransactionAuditTrail = forwardRef((props, ref) => {
  const theme = useTheme(); // Access the theme
  type Anchor = "top" | "left" | "bottom" | "right";
  const anchor: Anchor = "right";
  const [transactionData, setTransactionData] = React.useState<any>({});
  const [timelines, setTimelines] = React.useState<any>([]);
  const [isFetching, setIsFetching] = React.useState(true);
  const [showSummary, setShowSummary] = React.useState(true);
  const [transaction, setTransaction] = React.useState<any>({});
  const [isRefundedOrCancelled, setIsRefundedOrCancelled] = React.useState<
    boolean
  >(false);
  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false
  });
  const [isSurchargeEnabled, SetIsSurchargeEnabled] = React.useState<boolean>(
    false
  );
  const surchargeEnabled = useAppSelector(
    (state: RootState) => state.dealer.isSurchargeEnabled
  );
  React.useEffect(() => {
    if (
      surchargeEnabled !== "undefined" &&
      surchargeEnabled !== "null" &&
      surchargeEnabled !== null
    )
      SetIsSurchargeEnabled(surchargeEnabled);
    else SetIsSurchargeEnabled(false);
  })
  const isTerminal = transactionData?.paymentType === "Terminal";
  const isAch = transactionData?.paymentType === "ACH"

  const type = transactionData?.transactionType ?? "";
  const fundingSource = isAch ? " ACH Direct Debit": 
    type === "DebitSale" ? " Debit": 
    type === "CreditSale" ? " Credit" : " N/A";

  const status = (transaction?.status ?? transaction?.Status ?? "") as PaymentStatus;
  const showFundingSource = status === PaymentStatus.Success
     || status === PaymentStatus.Authorised
     || status === PaymentStatus.CardSaved
     || status === PaymentStatus.Refunded
     || status === PaymentStatus.PartialRefunded


  
  const normalizedStatus = status.toLowerCase();

  const formatResult = (code?: string, text?: string): string => {
    if (isEmpty(code) || code === "NA") return "Not Available (N/A)";
    if (isEmpty(text) || text === "NA") return "Not Available (N/A)";
    return `${text} (${code})`;
  };

  const avsResult = formatResult(
    transactionData?.avsResponseCode,
    transactionData?.avsResponseText
  );

  const cvvResult = formatResult(
    transactionData?.cvvResponseCode,
    transactionData?.cvvResponseText
  );

  // The component instance will be extended with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    openDrawer(transaction) {
      setIsFetching(true);
      const response = transaction?.Result ?? transaction?.result ?? null;
      const data = response ? JSON.parse(response) : null;
      setTransactionData(data?.transactionResponse ?? null);

      const isRefundedOrCancelled =
        transaction.status !== undefined
          ? transaction.status === PaymentStatus.Refunded ||
            transaction.status === PaymentStatus.Cancelled ||
            transaction.status === PaymentStatus.AutoReversal
          : false;

      setIsRefundedOrCancelled(isRefundedOrCancelled);

      var reqAmount =
        (transaction.RequestedAmount ?? 0) ||
        ((isRefundedOrCancelled
          ? Math.abs(transaction.transactionAmount)
          : transaction.transactionAmount) ??
          0);
      var platformFee =
        (transaction.PlatformFee ?? 0) ||
        ((isRefundedOrCancelled
          ? Math.abs(transaction.platformFee || transaction.surcharge)
          : transaction.platformFee || transaction.surcharge) ??
          0);

      transaction.TotalAmount = Math.abs(
        Number(reqAmount) + Number(platformFee)
      );
      setTransaction(transaction);
      getTimeLineData(transaction.PaymentId || transaction.uniqueTransId);
      toggleDrawer(anchor, true);
    }
  }));

  async function getTimeLineData(paymentId) {
    var resp = await TargetPayApi(
      `AuditTrail/${paymentId}`,
      undefined,
      "Get",
      "Transaction"
    );
    setTimelines(resp);
    setIsFetching(false);
  }

  const toggleDrawer = (anchor: Anchor, open: boolean) => {
    setState({ ...state, [anchor]: open });
  };

  const closeDrawer = (anchor: Anchor, open: boolean) => (
    event: React.KeyboardEvent | React.MouseEvent
  ) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    toggleDrawer(anchor, open);
  };

  const emails = ["username@gmail.com", "user02@gmail.com"];
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState(emails[1]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (value: string) => {
    setOpen(false);
    setSelectedValue(value);
  };

  const handleOnOrderClick = (orderId: string) => {
    Callback(
      JSON.stringify({ event: "onOrderClick", orderId: orderId }),
      GetDealerHostUrl()
    );
  };

  const resolvePaymentStatus = (status: string): PaymentStatus | string => {
    if (!status) return status;

    // Check if status matches any PaymentStatus enum value
    const statusKey = Object.keys(PaymentStatus).find(
      key =>
        PaymentStatus[key as keyof typeof PaymentStatus] === status ||
        key === status
    );

    return statusKey
      ? PaymentStatus[statusKey as keyof typeof PaymentStatus]
      : status;
  };

  return (
    <Drawer
      anchor={anchor}
      open={state[anchor]}
      onClose={closeDrawer(anchor, false)}
      PaperProps={{
        sx: {
          width: 405, // fixed width
          maxWidth: 405, // prevent expansion beyond fixed width
          overflowX: "hidden" // prevents horizontal expansion
        }
      }}
    >
      <Box className="audittrail" style={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={15}
          className="header"
          style={{
            backgroundColor: theme.palette.secondary.main
          }}
        >
          <h4>Transaction Timeline</h4>
          <Close
            onClick={closeDrawer(anchor, false)}
            sx={{ fontSize: "20px", color: "#c5c6c7", cursor: "pointer" }}
          ></Close>
        </Stack>
        <ProgressLoader isFetching={isFetching}></ProgressLoader>
        {!isFetching && (
          <Stack direction="column" className="content">
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Checkbox
                size="small"
                style={{ display: "none" }}
                checked={showSummary}
                onChange={e => {
                  setShowSummary(e.currentTarget.checked);
                }}
              />
              <label style={{ fontSize: "12px" }}>Learn about it</label>
              <IconButton onClick={handleClickOpen}>
                <Info style={{ fontSize: "18px" }}></Info>
              </IconButton>
            </Stack>
            <Stack direction="column" className="transaction-info">
              <div>
                Order#:{" "}
                <label
                  onDoubleClick={e =>
                    handleOnOrderClick(
                      transaction.InvoiceId || transaction.invoiceId
                    )
                  }
                >
                  {transaction.InvoiceId || transaction.invoiceId}
                </label>
              </div>
              <div>
                Reference:{" "}
                <label>
                  {transaction.PaymentId || transaction.uniqueTransId}
                </label>
              </div>
              <div>
                Customer:{" "}
                <label>
                  {transaction.CustomerName || transaction.customerName}
                </label>
              </div>
              {transactionData?.cashier?.trim() && (
                <div>
                  {" "}
                  Cashier: <label>{transactionData.cashier}</label>
                </div>
              )}
              <div>
                Amount:{" "}
                <label>
                  {convertToUSD(
                    transaction.RequestedAmount ||
                      (isRefundedOrCancelled
                        ? Math.abs(transaction.transactionAmount)
                        : transaction.transactionAmount)
                  )}
                </label>
              </div>
              {GetDealerPlatformFeeOption() &&
                (transaction.PlatformFeeTypeId === 0 ||
                  transaction.platformFeeTypeId === 0) && (
                  <div>
                    Platform fee:
                    <label>
                      {convertToUSD(
                        transaction.PlatformFeeTypeId === 0 ||
                          transaction.platformFeeTypeId === 0
                          ? transaction.PlatformFee ||
                              (isRefundedOrCancelled
                                ? Math.abs(transaction.platformFee)
                                : transaction.platformFee)
                          : 0
                      )}
                    </label>
                  </div>
                )}
              {isSurchargeEnabled &&
                (transaction.PlatformFeeTypeId === 1 ||
                  transaction.platformFeeTypeId === 1) && (
                  <div>
                    Surcharge Amount:
                    <label>
                      {convertToUSD(
                        transaction.PlatformFeeTypeId === 1 ||
                          transaction.platformFeeTypeId === 1
                          ? transaction.PlatformFee ||
                              (isRefundedOrCancelled
                                ? Math.abs(transaction.surcharge)
                                : transaction.surcharge)
                          : 0
                      )}
                    </label>
                  </div>
                )}
              {(GetDealerPlatformFeeOption() || isSurchargeEnabled) && (
                <div>
                  Total: <label>{convertToUSD(transaction.TotalAmount)}</label>
                </div>
              )}

              <div>
                Status:{" "}
                <PaymentStatusColumn
                  isRefunded={transaction.isRefunded || transaction.IsRefunded}
                  cancelOrRefund={
                    transaction.cancelOrRefund || transaction.CancelOrRefund
                  }
                  isCancelled={
                    transaction.isCancelled || transaction.IsCancelled
                  }
                  status={resolvePaymentStatus(
                    transaction.status || transaction.Status
                  )}
                  handleClick={() => {}}
                  refundInitiated={
                    transaction.refundInitiated || transaction.RefundInitiated
                  }
                  transactionTypeId={
                    transaction.transactionTypeId ?? transaction.TransactionTypeId
                  }
                  renderAsColumn={false}
                ></PaymentStatusColumn>
              </div>
              {(transaction.authCode || transaction.AuthCode) && (
                <div>
                  Authorization Code:{" "}
                  <label>{transaction.authCode || transaction.AuthCode}</label>
                </div>
              )}
              { showFundingSource &&  (
                <div>
                  Funding Source: <label>{fundingSource}</label>
                </div>
              )}
              {(transaction.cardLastFourNumber ||
                transaction.CardLastFourNumber) && (
                <div>
                  Ending In:{" "}
                  <label>
                    {transaction.cardLastFourNumber ||
                      transaction.CardLastFourNumber}
                  </label>
                </div>
              )}
              {(transaction.PaymentType || transaction.paymentType) && (
                <div>
                  Processing Method:{" "}
                  <label>
                    {ProcessingMethodLabels[
                      transaction.paymentType || transaction.PaymentType
                    ] ??
                      (transaction.PaymentType || transaction.paymentType)}
                  </label>
                </div>
              )}
              {isTerminal && (
                <div>
                  Terminal Name:{" "}
                  <label>
                    {transaction?.TerminalId ??
                      transaction?.terminalId ??
                      " N/A"}
                  </label>
                </div>
              )}
              <div>
                Transaction Date:{" "}
                <label>
                  {isEmpty(transaction.DateProcessed)
                    ? !isEmpty(transaction.DateCreated)
                      ? transaction.DateCreated.toUSDateFormat()
                      : transaction.transactionDate.toUSDateFormat()
                    : transaction.DateProcessed.toUSDateFormat()}
                </label>
              </div>
              <div>
                <Tooltip title="Address Verification System">
                  <span>AVS Result: </span>
                </Tooltip>
                <label style={wrapTextStyle}>{avsResult}</label>
              </div>
              <div>
                CVV Result: <label style={wrapTextStyle}>{cvvResult}</label>
              </div>
              {[...(transaction.customFields || [])]
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((field: any, index: number) => (
                  <div key={`custom-field-${index}`}>
                    {field.label}: <label>{field.value}</label>
                  </div>
                ))}
            </Stack>
            <br />
            <TransactionAuditTrailTimeline
              timelines={timelines}
              showSummary={showSummary}
            ></TransactionAuditTrailTimeline>
          </Stack>
        )}
      </Box>
      <AuditTrailTimelineHelp
        timelines={timelines}
        selectedValue={selectedValue}
        open={open}
        onClose={handleClose}
      ></AuditTrailTimelineHelp>
    </Drawer>
  );
});

export default TransactionAuditTrail;
